import { tt } from '@agos/shared'

/**
 * Whether push notifications can actually deliver — FB-C03.
 *
 * Measured on this stack: `GET /api/v1/push/public-key` answers `{"publicKey": ""}` — no VAPID key is
 * configured — while `customer_push_subscriptions` holds **114** rows. So 114 browsers are registered
 * for a channel that cannot send.
 *
 * The backend is careful about this. Its controller says an empty answer *"is how the frontend learns
 * push is not configured here"*, and `bulk-push` reports what it *could* have reached rather than
 * claiming a send, because *"a screen that says 'sent to 400' when nothing left the building is worse
 * than one that says nothing was sent."*
 *
 * The customer app is not. `getVapidPublicKey` turns the empty string into `null` so `enablePush` fails
 * correctly — but `getPushState` never asks for the key at all, deciding `granted-active` purely from
 * `pushManager.getSubscription()`. Every browser that subscribed before the key went missing is told
 * push is working.
 *
 * So `unconfigured` is a state here, distinct from *denied* and from *on*, and the banner does not offer
 * to enable a channel that cannot deliver.
 */

export type PushState =
  | 'unsupported'
  | 'unconfigured'
  | 'denied'
  | 'default'
  | 'granted-active'
  | 'granted-inactive'

export function hasVapidKey(publicKey: unknown): boolean {
  return typeof publicKey === 'string' && publicKey.trim() !== ''
}

/**
 * The VAPID key as the server actually sends it.
 *
 * `GET /push/public-key` answers `{ "publicKey": "" }` on this data — a 200 with an empty string, not
 * a 404 — so the payload is well-formed and the key is absent. Reading it has to keep those two apart:
 * `null` here means *the server did not answer with a key*, and an empty string means *it did, and
 * there is none configured*. Both end up as "cannot deliver", but only after {@link hasVapidKey} has
 * said so, and that is the check legacy never makes.
 *
 * **Beside `hasVapidKey` since §9.239**, because the profile needs it as much as the banner does and a
 * package cannot import a module. `customer-notifications` re-exports this one rather than keeping a
 * second copy.
 */
export function publicKeyOf(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null) return null
  const value = (payload as { publicKey?: unknown }).publicKey
  return typeof value === 'string' ? value : null
}

/**
 * The state, from the four things that decide it.
 *
 * The key is checked before the browser's own answers, deliberately: a browser that supports push, has
 * granted permission and holds a subscription still receives nothing if nothing can sign a message.
 */
export function pushState(input: {
  supported: boolean
  publicKey: unknown
  permission: 'granted' | 'denied' | 'default'
  hasSubscription: boolean
}): PushState {
  if (!input.supported) return 'unsupported'
  if (!hasVapidKey(input.publicKey)) return 'unconfigured'
  if (input.permission === 'denied') return 'denied'
  if (input.permission === 'default') return 'default'
  return input.hasSubscription ? 'granted-active' : 'granted-inactive'
}

/** Whether offering to switch push on can lead anywhere. */
export function mayEnable(state: PushState): boolean {
  return state === 'default' || state === 'granted-inactive'
}

/**
 * What the profile's push row is — §9.239, FB-397.
 *
 * The row was a **statement**: a fixed *Zapnuté/Vypnuté* label, on the argument that a checkbox which
 * sometimes opens a permission dialog and sometimes does nothing is worse than a fact. That argument
 * is right for `unconfigured` — this stack's own state, where no VAPID key exists and enabling cannot
 * work whatever the customer presses — and wrong for `granted-active`, where switching **off** needs
 * no prompt, always succeeds, and is the one action a consent screen has to offer. Legacy has had the
 * switch since the port; AGOS had the endpoint and no caller.
 *
 * `denied` draws the box disabled rather than hiding it, as legacy does: a customer who blocked
 * notifications in the browser should see a row saying so.
 */
export interface PushToggle {
  /** Whether to draw a checkbox at all, as against the sentence alone. */
  shown: boolean
  checked: boolean
  disabled: boolean
  /** What pressing it does, or `null` when it does nothing. */
  action: 'enable' | 'disable' | null
}

export function pushToggle(state: PushState, busy = false): PushToggle {
  // Nothing to offer: the browser cannot, or the server has no key to sign with.
  if (state === 'unsupported' || state === 'unconfigured') {
    return { shown: false, checked: false, disabled: true, action: null }
  }
  if (state === 'denied') return { shown: true, checked: false, disabled: true, action: null }
  const active = state === 'granted-active'
  return {
    shown: true,
    checked: active,
    disabled: busy,
    action: active ? 'disable' : 'enable',
  }
}

export function pushNote(state: PushState): string {
  if (state === 'granted-active') return tt('Upozornenia máte zapnuté.')
  if (state === 'unsupported') return tt('Tento prehliadač upozornenia nepodporuje.')
  if (state === 'unconfigured') {
    // Said plainly rather than as an error: it is the restaurant's configuration, not the customer's
    // browser, and there is nothing they can do about it.
    return tt('Upozornenia zatiaľ nie sú spustené - reštaurácia ich ešte nemá nastavené.')
  }
  if (state === 'denied') {
    return tt('Upozornenia sú zablokované - povoliť sa dajú v nastaveniach prehliadača.')
  }
  return tt('Chcete vedieť, keď je objednávka na ceste?')
}

/** The base64url VAPID key as the subscription API wants it. */
export function vapidKeyBytes(publicKey: string): Uint8Array {
  const padded = publicKey + '='.repeat((4 - (publicKey.length % 4)) % 4)
  const base64 = padded.replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const bytes = new Uint8Array(raw.length)
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index)
  return bytes
}
